import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useHumanizeFileSizeUtils from 'Vendor/Utils/HumanizeFileSizeUtils';
import _ModalComments from '../../Modal/_ModalMentionComments';
import { addQueryParams } from 'Vendor/Utils/UrlUtils';
import Mention from 'Entity/Mention';
import File from 'Entity/File';
import Version from 'Entity/File/Version';
import DeleteModal from 'Components/Modal/DeleteModal';
import {BannerContext} from "Vendor/Context/BannerContextProvider";

type FileVersionProps = {
    comments: Mention[]
    file: File;
    setComments: Dispatch<SetStateAction<Mention[]>>
    version: Version;
    handleOffCanvasClose: () => void;
    changeStateOnEdit: (status: boolean) => void;
};

const _FileVersion: FC<FileVersionProps> = ({ comments, file, setComments, version, handleOffCanvasClose, changeStateOnEdit }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { humanFileSize } = useHumanizeFileSizeUtils();
    const { setBanner } = useContext(BannerContext);

    let date = '';
    let time = '';

    const [showDelete, setShowDelete] = useState<boolean>(false)
    const handleDeleteClose = () => setShowDelete(false);

     const handleSuccess = () => {
        setShowDelete(false)
        setOpen(false)
        handleOffCanvasClose()
        changeStateOnEdit(true)
        setBanner({type: 'success', i18nTitle: 'app.save', i18nMessage: 'app.data_saved_message'})
    }

    if (version.createdAt) {
        const createdAt = new Date(version.createdAt);
        date = createdAt.toLocaleDateString('fr-FR');
        time = createdAt.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    const dimensions = version?.width && version?.height
        ? `${version.width}x${version.height} pixels`
        : t('app.empty');
    const resolution = version.resolution ? `${version.resolution} ppp` : t('app.empty');
    const format = version.format || t('app.empty');
    const sizeMB = humanFileSize(version.size || 0);
    const software = version.software || t('app.empty');

    const handleDeleteShow = () => setShowDelete(true)

    return (
        <div className={`version mb-3 bg-white p-3 rounded ${open ? 'open' : ''}`}>
            <div
                className="version-header d-flex justify-content-between align-items-center"
                onClick={() => setOpen(!open)}
                style={{ cursor: 'pointer' }}
            >
                <div className="fs-5">
                    {t('app.revision_label', { date, time })}
                </div>
                <button className="btn btn-link text-dark">
                    <i className={`fas fa-chevron-${open ? 'up' : 'down'}`}></i>
                </button>
            </div>

            {open && (
                <div className='d-flex flex-column mt-4'>
                    <div
                        className="version-item d-flex p-2 w-100 align-items-start justify-content-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="image-preview me-4 w-100">
                            <img
                                src={version.thumbnail?.replaceAll('__SIZING__', `${version.versionNumber}_extra-large`)}
                                alt={`Version ${version.versionPosition}`}
                                className="img-fluid"
                            />
                        </div>

                        <div className="d-flex flex-column w-100">
                            <div className="d-flex justify-content-end gap-2 mb-2">
                                <a
                                    href={addQueryParams(`${file.view}`, `?download=1&version=${version.versionNumber}`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-lg btn-dark"
                                    title="Download"
                                >
                                    <i className="fas fa-download"></i>
                                </a>

                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-lg btn-danger"
                                    onClick={handleDeleteShow}
                                    title="Delete"
                                >
                                    <i className="fas fa-trash"></i>
                                </a>
                            </div>
                            {file && file.mentionPropertyEnabled?.enabled && <_ModalComments comments={comments} setComments={setComments} entity={file} entityType="file" disabled />}
                        </div>
                    </div>
                    <div className="metadata fs-6">
                        {[
                            { label: t('app.software'), value: software },
                            { label: t('app.size'), value: sizeMB },
                            { label: t('app.dimensions'), value: dimensions },
                            { label: t('app.resolution'), value: resolution },
                            { label: t('app.format'), value: format },
                        ].map((item, index) => (
                            <div key={index} className="row mb-2">
                                <div className="col-3 fw-semibold">{item.label}:</div>
                                <div className="col-9 text-muted">{item.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <DeleteModal
                handleClose={handleDeleteClose}
                show={showDelete}
                path={`/file/${file.id}/version/${version.id}/delete`}
                name={'#V' + (version.versionPosition ?? 'error')}
                archive={true}
                onSuccess={handleSuccess}
            />
        </div>
    );
};

export default _FileVersion;
